import { Badge, Button } from "../ui";
import { useState } from "react";
import { ctaTranslate } from "../lib/ctaTranslate";
import {
  handleProductImageError,
  resolveProductImageSrc,
} from "../lib/fallback-image";
import { useLabel } from "../lib/labels";
import { formatMoney } from "../lib/money";
import { ProductNameText } from "../lib/ProductNameText";
import { productDisplayName } from "../lib/productText";
import type { PromotionProductsData, PromotionProductGroup, ProductListItem } from "../types";
import { cartSkuForProduct } from "./pick/cartSku";

interface Props {
  data: PromotionProductsData;
  sessionToken: string;
}

const LG_IMG_CDN = "https://www.lg.com/content/dam/channel/wcms";

type PriceBlock = NonNullable<ProductListItem["price"]>;

function normalizeImageUrl(path?: string): string {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${LG_IMG_CDN}${path.startsWith("/") ? "" : "/"}${path}`;
}

function priceAmt(p: ProductListItem["price"] | undefined): number | undefined {
  if (p == null) return undefined;
  return p.amount;
}

function priceSym(p: ProductListItem["price"] | undefined, fb = "£"): string {
  if (p && p.symbol) return p.symbol;
  return fb;
}

function fmt(p: ProductListItem["price"] | undefined, fb = "£"): string {
  const a = priceAmt(p);
  if (a == null || !Number.isFinite(a) || a <= 0) return "—";
  return formatMoney(a, priceSym(p, fb));
}

function fmtPlain(amount: number | undefined, sym: string): string {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return "";
  return formatMoney(amount, sym);
}

function ProductItem({
  item,
  sessionToken,
}: {
  item: ProductListItem;
  sessionToken: string;
}) {
  const imgUrl = normalizeImageUrl(item.image);
  const priceBlock = item.price as PriceBlock | undefined;
  const sym = priceSym(item.price);
  const membersOnlyTpl = useLabel("membersOnlyPrice", "Members Only {price}");
  const addToCartLabel = useLabel("addToCart", "Add to Cart");
  const displayName = productDisplayName(item.name, item.sku);

  const handleDetail = () => {
    void ctaTranslate({
      code: "promo_products.tell_me_about",
      params: { sku: item.sku },
      sessionToken,
      fallback: `Tell me about ${item.sku}`,
    });
  };

  const handleAddToCart = () => {
    const cartSku = cartSkuForProduct(item);
    void ctaTranslate({
      code: "promo_products.add_sku_to_cart",
      params: { sku: item.sku },
      sessionToken,
      fallback: `Add ${item.sku} to cart`,
      toolCall: {
        tool: "commerce_add_to_cart",
        args: { sku: cartSku },
      },
    });
  };

  return (
    <div className="ac-promo-products__item">
      <img
        src={resolveProductImageSrc(imgUrl)}
        alt={displayName}
        className="ac-promo-products__item-img"
        loading="lazy"
        draggable={false}
        onClick={handleDetail}
        onError={handleProductImageError}
        style={{ cursor: "pointer" }}
      />
      <div className="ac-promo-products__item-body">
        <div
          className="ac-promo-products__item-name ac-clamp-2"
          onClick={handleDetail}
          style={{ cursor: "pointer" }}
        >
          <ProductNameText name={item.name} sku={item.sku} />
        </div>
        <div className="ac-promo-products__item-sku">{item.sku}</div>
        <div className="ac-promo-products__item-price-wrap">
          <div className="ac-promo-products__item-price">{fmt(item.price)}</div>
          {priceBlock?.membershipPrice && priceBlock.membershipPrice > 0 ? (
            <div className="ac-promo-products__item-price-sub">
              {membersOnlyTpl.replace(
                "{price}",
                fmtPlain(priceBlock.membershipPrice, sym),
              )}
            </div>
          ) : priceBlock?.originalAmount &&
            priceBlock.originalAmount > (priceBlock.amount ?? 0) ? (
            <div className="ac-promo-products__item-price-sub ac-promo-products__item-price-sub--strike">
              {fmtPlain(priceBlock.originalAmount, sym)}
            </div>
          ) : null}
        </div>
        <Button
          variant="primary"
          size="xs"
          fullWidth
          className="ac-promo-cta"
          onClick={handleAddToCart}
        >
          {addToCartLabel}
        </Button>
      </div>
    </div>
  );
}

function PromotionGroup({
  group,
  defaultOpen,
  sessionToken,
}: {
  group: PromotionProductGroup;
  defaultOpen: boolean;
  sessionToken: string;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const itemCountOneTpl = useLabel("itemCountOne", "{n} item");
  const itemCountManyTpl = useLabel("itemCountMany", "{n} items");
  const countTpl =
    group.productCount === 1 ? itemCountOneTpl : itemCountManyTpl;

  return (
    <div className="ac-promo-products__group">
      <button
        type="button"
        className="ac-promo-products__header"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
      >
        <span className="ac-promo-products__chevron" data-open={isOpen}>
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </span>
        <span className="ac-promo-products__title ac-clamp-1">
          {group.promotionTitle}
        </span>
        {group.promotionBadge && (
          <Badge tone="deal">{group.promotionBadge}</Badge>
        )}
        <span className="ac-promo-products__count">
          {countTpl.replace("{n}", String(group.productCount))}
        </span>
      </button>
      {isOpen && (
        <div className="ac-promo-products__grid">
          {group.products.map((p) => (
            <ProductItem key={p.sku} item={p} sessionToken={sessionToken} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PromotionProductsCard({ data, sessionToken }: Props) {
  const noProductsLabel = useLabel(
    "noProductsFound",
    "No products found for these promotions.",
  );
  if (!data.groups?.length) {
    return (
      <div className="ac-promo-products">
        <div className="ac-empty">{noProductsLabel}</div>
      </div>
    );
  }

  return (
    <div className="ac-promo-products">
      {data.groups.map((group, i) => (
        <PromotionGroup
          key={group.promotionId}
          group={group}
          defaultOpen={i === 0}
          sessionToken={sessionToken}
        />
      ))}
    </div>
  );
}
